import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  DropdownFooter,
  DropdownOverlay,
  InfoIcon,
  PlusIcon,
  SelectInput,
  TableCell,
  TableEditableDropdownCell,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { getSearchDropdownDescription } from 'components/RunPayroll/Popups/EditSalary/utils';
import { useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { SalaryComponent } from 'components/SalaryComponents/types';
import React from 'react';
import { useSalaryStructure } from '../context/SalaryStructureProvider';
import { SalaryStructureComponent, SalaryStructureComponentProps } from '../types';

interface ComponentCellProps extends SalaryStructureComponentProps {
  searchSalaryComponents: (ignoreKeys?: string[]) => SalaryComponent[];
}

const ComponentCell = ({ item, searchSalaryComponents }: ComponentCellProps) => {
  const id = item.salaryComponent.id;

  const { openEarningsCreation } = useSalaryComponentHelpers();

  const isSpecialAllowance =
    item.salaryComponent.componentType === 'COMPONENT_TYPE_SPECIAL_ALLOWANCE';
  const isBasicSalary = item.salaryComponent.componentType === 'COMPONENT_TYPE_BASIC';

  const displayName =
    item.salaryComponent.settings.behaviour.displayBehaviour?.displayName ||
    item.salaryComponent.name;

  let description = '';
  if (isSpecialAllowance) {
    description = `${displayName} is a residual component of salary that is left after allocations are made for all other components.`;
  } else if (isBasicSalary) {
    description = '';
  }

  const { updateComponent, salaryStructureComponents } = useSalaryStructure();

  const handleComponentChange = (value?: SalaryComponent) => {
    if (value) {
      const updatedComponent: SalaryStructureComponent = {
        ...item,
        id: value.id,
        salaryComponent: value,
        valueType:
          value.category === 'EARNINGS_CATEGORY' &&
          value.settings.earningDetails?.payType === 'fixed'
            ? 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED'
            : 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_CTC',
      };
      updateComponent(item.id.toString(), updatedComponent);
    }
  };

  if (isSpecialAllowance || isBasicSalary) {
    return (
      <TableCell>
        <Box display="flex" alignItems="center" paddingX="spacing.2" paddingY="spacing.5">
          <Text size="large" color="surface.text.gray.subtle">
            {displayName}
          </Text>
          {description ? (
            <Tooltip content={description}>
              <TooltipInteractiveWrapper>
                <InfoIcon marginLeft="spacing.2" size="medium" />
              </TooltipInteractiveWrapper>
            </Tooltip>
          ) : null}
        </Box>
      </TableCell>
    );
  }

  const ignoreKeys = salaryStructureComponents
    .filter((data) => data.salaryComponent.id !== item.salaryComponent.id)
    .map((data) => data.salaryComponent.id);

  const options = searchSalaryComponents(ignoreKeys);

  return (
    <TableEditableDropdownCell>
      <SelectInput
        label=""
        placeholder="Salary Component"
        accessibilityLabel="Salary Component"
        value={id}
        onChange={(e) => handleComponentChange(options.find((option) => option.id === e.values[0]))}
      />
      <DropdownOverlay width="300px">
        <ActionList>
          {options.map((option) => (
            <ActionListItem
              key={option.id}
              title={option.settings.behaviour.displayBehaviour?.displayName || option.name}
              description={getSearchDropdownDescription(item.salaryComponent)}
              value={option.id}
            />
          ))}
        </ActionList>
        <DropdownFooter>
          <Button
            isFullWidth
            variant="tertiary"
            size="small"
            icon={PlusIcon}
            onClick={(e) => openEarningsCreation()}>
            Create addition
          </Button>
        </DropdownFooter>
      </DropdownOverlay>
    </TableEditableDropdownCell>
  );
};

ComponentCell.displayName = 'ComponentCell';

export default React.memo(ComponentCell);
